import React, { useMemo } from 'react'

import {
  PromDataAccessor,
  PromQueryGroup,
  TimeSeriesChart,
  Query
} from '@diag-ui/chart'
import {
  Accordion,
  Button,
  Code,
  Divider,
  Grid,
  Group,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip
} from '@mantine/core'
import { IconInfoCircle, IconQuestionMark } from '@tabler/icons-react'
import { Link, useParams } from 'react-router-dom'
import useSWR from 'swr'

import {
  getNodeInstance,
  grafanaQueryRangeForControlPlane,
  getOOMSummary,
  getOOMDetail,
  getDataMetrics
} from '~/api'
import { Header } from '~/componentv2/Header'
import { useCurCluster, useCurOrg } from '~/hooks/state'
import { oomInsightTelemetry } from '~/telemetry/oom-insight'
import {
  formatDateTime,
  formatDateTimeWithTz,
  getLocalTzStr
} from '~/utils/date-time'
import envConfig from '~/utils/env-config'
import { getLokiFullPath } from '~/utils/links'

import { replaceQuery } from '../shared/utils/promql'
import ExpandableSQL from './components/ExpandableSQL'

// Spec: TiDB OOM Insight
// https://pingcap.feishu.cn/wiki/wikcnBMUa1XMdntn9RU5YaxWwtd

////////////////////////////////

type ItemCompProps = {
  oomSummary?: any
  oomDetail?: any
  oomEvent?: any
  tHappen: number
}

////////////////////////////////

const localTimeZoneStr = getLocalTzStr()

////////////////////////////////

const downloadTooltip =
  'If there is no file on the download page, please inquire at Ask Clinic Group'

const DownloadLinkWithTooltip: React.FC<{
  tHappen: number
  orgId: string
  cid: string
  forContent: string
}> = ({ tHappen, orgId, cid, forContent }) => {
  return (
    <Tooltip
      maw={400}
      withArrow
      multiline
      position='top'
      label={downloadTooltip}
    >
      <Group spacing='xs' className='w-fit'>
        <Text
          className='underline'
          component={Link}
          to={`/orgs/${orgId}/clusters/${cid}/files?beginTime=${
            tHappen - 24 * 60
          }&endTime=${tHappen}&notEmpty=true`}
          onClick={() =>
            oomInsightTelemetry.clickDownloadFiles({
              source: forContent
            })
          }
        >
          Download Files
        </Text>
        <ThemeIcon
          variant='default'
          size='xs'
          radius='xl'
          className='border-none'
        >
          <IconInfoCircle />
        </ThemeIcon>
      </Group>
    </Tooltip>
  )
}

////////////////////////////////

const relatedDocs = [
  {
    text: 'TiDB Memory Control',
    link: 'https://docs.pingcap.com/tidb/dev/configure-memory-usage'
  },
  {
    text: 'TiDB Memory Control Troubleshoot TiDB OOM Issues',
    link: 'https://docs.pingcap.com/tidb/dev/troubleshoot-tidb-oom'
  }
]

const OOMInfo: React.FC<ItemCompProps> = ({ oomSummary, oomDetail }) => {
  const summary = oomSummary?.payload.summary
  const evidences = oomDetail?.evidences

  return (
    <Stack>
      {summary ? (
        <Text>{summary}</Text>
      ) : (
        <Skeleton height={24} animate={false} />
      )}

      {evidences ? (
        <Stack spacing={4}>
          <Text>The following logs are found in {evidences.source}:</Text>
          <Code className='p-2'>{evidences.content}</Code>
        </Stack>
      ) : (
        <Skeleton height={48} animate={false} />
      )}

      <Text>
        Some related documents you need to know:{' '}
        {relatedDocs.map((d, idx) => (
          <span key={d.text}>
            <Text
              key={d.text}
              component='a'
              href={d.link}
              className='underline'
              target='_blank'
              onClick={() =>
                oomInsightTelemetry.clickRelatedDoc({ doc_link: d.link })
              }
            >
              {d.text}
            </Text>
            {idx === 0 && ', '}
          </span>
        ))}
        .
      </Text>
    </Stack>
  )
}

////////////////////////////////

type Chart = {
  name: string
  unit: string
  queries: Query[]
}

const charts: Chart[] = [
  {
    name: 'Memory',
    unit: 'bytes',
    queries: [
      {
        name: 'process',
        promql: `process_resident_memory_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}}`,
        type: 'line'
      },
      {
        name: 'gc-threshold',
        promql: `go_memstats_next_gc_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}}`,
        type: 'line'
      },
      {
        name: 'heap-inuse',
        promql: `go_memstats_heap_inuse_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}, component="tidb"}`,
        type: 'line'
      }
    ]
  },
  // {
  //   name: 'Node Memory',
  //   unit: 'bytes',
  //   queries: [
  //     {
  //       name: 'memory used',
  //       promql: `max(
  //         node_memory_MemTotal_bytes{job="node-exporter", {label_node_instance}}
  //         - node_memory_MemFree_bytes{job="node-exporter", {label_node_instance}}
  //         - node_memory_Buffers_bytes{job="node-exporter", {label_node_instance}}
  //         - node_memory_Cached_bytes{job="node-exporter", {label_node_instance}}
  //       )`,
  //       type: 'line'
  //     }
  //   ]
  // },
  {
    name: 'Uptime',
    unit: 's',
    queries: [
      {
        name: '{instance}',
        promql: `(time() - process_start_time_seconds{k8s_cluster="", {label_cluster_id}, {label_instance_name}, component="tidb"})`,
        type: 'line'
      }
    ]
  },
  {
    name: 'QPS',
    unit: 'short',
    queries: [
      {
        name: '{type}',
        promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m])) by (type)`,
        type: 'line'
      },
      {
        name: 'Total',
        promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
        type: 'line'
      },
      {
        name: 'Failed',
        promql: `sum(rate(tidb_server_execute_error_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
        type: 'line'
      }
    ]
  },
  {
    name: 'Connection Count',
    unit: 'short',
    queries: [
      {
        name: '{instance}',
        promql:
          'tidb_server_connections{k8s_cluster="", {label_cluster_id}, {label_instance_name}}',
        type: 'line'
      }
    ]
  },
  {
    name: 'CPS by Instance',
    unit: 'short',
    queries: [
      {
        name: '{instance} {type} {result}',
        promql:
          'rate(tidb_server_query_total{k8s_cluster="", {label_cluster_id}, {label_instance_name}}[2m])',
        type: 'line'
      }
    ]
  }
]

const OOMMetrics: React.FC<ItemCompProps> = ({
  tHappen,
  oomSummary,
  oomEvent
}) => {
  const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()
  const { orgType, isCloud } = curOrg!

  const { data: nodeInstance } = useSWR(
    tHappen > 0
      ? ['node_instance', oomSummary?.payload.instance, curCluster?.id]
      : null,
    () =>
      getNodeInstance({
        pod: oomSummary?.payload.instance,
        cluster: curCluster!
      })
  )

  return (
    <Grid>
      {charts.map((c) => (
        <Grid.Col md={6} key={c.name}>
          <div>
            <Title order={6} className='text-center pb-2'>
              {c.name}
            </Title>
            {(c.name === 'Node Memory' && !nodeInstance) || tHappen === 0 ? (
              <Skeleton height={200} animate={false} />
            ) : (
              <>
                <PromDataAccessor
                  fetch={(query, tp) => {
                    const _query = replaceQuery(
                      query,
                      curCluster!.id!,
                      orgType,
                      curCluster?.deployType,
                      oomSummary?.payload.instance,
                      nodeInstance
                    )
                    if (c.name === 'Node Memory') {
                      return grafanaQueryRangeForControlPlane({
                        cluster: curCluster!,
                        query: _query,
                        start: tp.start_time,
                        end: tp.end_time,
                        step: tp.step
                      }).then((res) => res.data)
                    } else {
                      return getDataMetrics({
                        query: _query,
                        orgId: curOrg!.id!,
                        clusterId: curCluster!.id!,
                        start: tp.start_time,
                        end: tp.end_time,
                        step: tp.step.toString() + 's',
                        isCloud
                      }).then((res) => res.data)
                    }
                  }}
                  params={{
                    start_time: tHappen - 20 * 60,
                    end_time: tHappen + 5 * 60
                  }}
                >
                  <TimeSeriesChart
                    height={200}
                    annotations={[
                      {
                        type: 'line',
                        start: [tHappen * 1000, 'min'],
                        end: [tHappen * 1000, 'max'],
                        style: {
                          lineDash: [4, 4]
                        },
                        top: true,
                        text: {
                          content: oomEvent?.display_name || oomEvent?.name
                        }
                      }
                    ]}
                  >
                    <PromQueryGroup queries={c.queries} unit={c.unit} />
                  </TimeSeriesChart>
                </PromDataAccessor>
              </>
            )}
          </div>
        </Grid.Col>
      ))}
    </Grid>
  )
}

////////////////////////////////

const OOMSQLs: React.FC<ItemCompProps> = ({
  oomSummary,
  oomDetail,
  oomEvent,
  tHappen
}) => {
  const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()

  const top10MemSQLs: any[] = useMemo(
    () => (oomDetail?.running_sqls_topmem ?? []).slice(0, 10),
    [oomDetail?.running_sqls_topmem]
  )
  const top10TimeSQLs: any[] = useMemo(
    () => (oomDetail?.running_sqls_toptime ?? []).slice(0, 10),
    [oomDetail?.running_sqls_toptime]
  )
  const latest10ExpensiveSQLs: any[] = useMemo(
    () =>
      (oomDetail?.expensive_sqls ?? [])
        .sort((a: any, b: any) => b.time - a.time)
        .slice(0, 10)
        .map((s: any) => {
          const recordTime = s.time
          // parseFloat('60.52s') = 60.52
          // Number('60.52s') = NaN
          const runningTime = parseFloat(s.content.cost_time)
          return { ...s, start_time: recordTime - runningTime }
        }),
    [oomDetail?.expensive_sqls]
  )
  const expensiveSQLDetaiLink = useMemo(() => {
    const dataSource = `Loki-${curCluster?.provider}-${curCluster?.region}`
    const expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", namespace=\\"tidb${oomDetail?.cluster_id}\\", instance=\\"${oomDetail?.instance}\\"} |= \`expensive_query\` |= \`WARN\``
    return getLokiFullPath(
      dataSource,
      expr,
      (tHappen - 15 * 60) * 1000,
      (tHappen + 1) * 1000
    )
  }, [tHappen, oomDetail?.cluster_id, oomDetail?.instance])

  return (
    <div>
      <div>
        <Group position='apart' py={8}>
          <Text>Running SQLs From OOM Files</Text>
          {tHappen > 0 && (
            <DownloadLinkWithTooltip
              tHappen={tHappen}
              orgId={curOrg?.id!}
              cid={curCluster?.id!}
              forContent='running sqls'
            />
          )}
        </Group>

        {!oomDetail ? (
          <Skeleton height={80} animate={false} />
        ) : (
          <Grid>
            <Grid.Col sm={6}>
              <Paper shadow='xs' withBorder>
                <Table>
                  <thead>
                    <tr>
                      <th>Top 10 Memory usage SQLs</th>
                      <th>Memory</th>
                      <th>Running Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top10MemSQLs.map((s, idx) => (
                      <tr key={idx}>
                        <td className='max-w-[150px]'>
                          <ExpandableSQL sql={s.content.sql} />
                        </td>
                        <td>{s.content.mem_max_human || s.content.mem_max}</td>
                        <td>{s.content.cost_time}</td>
                      </tr>
                    ))}
                    {top10MemSQLs.length < 10 && (
                      <tr>
                        <td>
                          <Text color='dimmed'>No more</Text>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Paper>
            </Grid.Col>
            <Grid.Col sm={6}>
              <Paper shadow='xs' withBorder>
                <Table>
                  <thead>
                    <tr>
                      <th>Top 10 Time usage SQLs</th>
                      <th>Running Time</th>
                      <th>Memory</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top10TimeSQLs.map((s, idx) => (
                      <tr key={idx}>
                        <td className='max-w-[150px]'>
                          <ExpandableSQL sql={s.content.sql} />
                        </td>
                        <td>{s.content.cost_time}</td>
                        <td>{s.content.mem_max_human || s.content.mem_max}</td>
                      </tr>
                    ))}
                    {top10TimeSQLs.length < 10 && (
                      <tr>
                        <td>
                          <Text color='dimmed'>No more</Text>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Paper>
            </Grid.Col>
          </Grid>
        )}
      </div>

      <div className='-mx-4'>
        <Divider my='sm' />
      </div>

      <div>
        <Group position='apart' py={8}>
          <Group spacing='xs'>
            <Text>Expensive SQLs from Logs</Text>
            <Tooltip
              maw={400}
              withArrow
              multiline
              position='right'
              label='Statements whose execution time exceeds tidb_expensive_query_time_threshold (60 seconds by default) or memory usage exceeds tidb_mem_quota_query (1 GB by default)'
            >
              <ThemeIcon variant='default' size='xs' radius='xl'>
                <IconQuestionMark />
              </ThemeIcon>
            </Tooltip>
          </Group>

          {tHappen && (
            <Text
              className='underline'
              component='a'
              href={expensiveSQLDetaiLink}
              target='_blank'
              onClick={() => oomInsightTelemetry.clickExpensiveSQLsDetail()}
            >
              More Details
            </Text>
          )}
        </Group>

        {!oomDetail ? (
          <Skeleton height={80} animate={false} />
        ) : (
          <Table horizontalSpacing={0}>
            <thead>
              <tr>
                <th>
                  Latest 10 Expensive SQLs Before{' '}
                  {oomEvent?.display_name || oomEvent?.name}
                </th>
                <th>Record Time ({localTimeZoneStr})</th>
                <th>Running Time</th>
                <th>Start Time ({localTimeZoneStr})</th>
                <th>Memory</th>
                <th>Coprocessor Tasks</th>
              </tr>
            </thead>
            <tbody>
              {latest10ExpensiveSQLs.map((s, idx) => (
                <tr key={idx}>
                  <td className='max-w-xs'>
                    <div className='pr-2'>
                      <ExpandableSQL
                        sql={s.content.sql}
                        tooltipPlacement='left'
                      />
                    </div>
                  </td>
                  <td>{formatDateTime(s.time)}</td>
                  <td>{s.content.cost_time}</td>
                  <td>{formatDateTime(s.start_time)}</td>
                  <td>{s.content.mem_max_human || s.content.mem_max}</td>
                  <td>{s.content.num_cop_tasks}</td>
                </tr>
              ))}
              {latest10ExpensiveSQLs.length < 10 && (
                <tr>
                  <td>
                    <Text color='dimmed'>No more</Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className='-mx-4'>
        <Divider my='sm' />
      </div>

      <Table horizontalSpacing={0}>
        <tbody>
          <tr>
            <td>From Top SQL</td>
            <td>
              <Text
                className='underline'
                component='a'
                href={`dashboard/cloud/ngm.html?provider=${
                  curCluster?.provider
                }&region=${curCluster?.region}&orgId=${
                  curOrg?.externalID
                }&projectId=${curCluster?.projectID}&clusterId=${
                  curCluster?.id
                }&deployType=${curCluster?.deployType}&version=${
                  curCluster?.version || ''
                }#/topsql?from=${
                  tHappen - 15 * 60
                }&to=${tHappen + 5 * 60}&timeRangeType=absolute&instance=${
                  oomSummary?.payload.instance
                }`}
                target='_blank'
                onClick={() => oomInsightTelemetry.clickTopSQLDetail()}
              >
                More Details on {formatDateTime(tHappen - 15 * 60)} ~{' '}
                {formatDateTimeWithTz(tHappen + 5 * 60)}
              </Text>
            </td>
          </tr>
          <tr>
            <td>From Slow Query</td>
            <td>
              <Text
                className='underline'
                component='a'
                href={`dashboard/cloud/ngm.html?provider=${
                  curCluster?.provider
                }&region=${curCluster?.region}&orgId=${
                  curOrg?.externalID
                }&projectId=${curCluster?.projectID}&clusterId=${
                  curCluster?.id
                }&deployType=${curCluster?.deployType}&version=${
                  curCluster?.version || ''
                }#/slow_query?from=${
                  tHappen - 15 * 60
                }&to=${tHappen + 5 * 60}&timeRangeType=absolute`}
                target='_blank'
                onClick={() => oomInsightTelemetry.clickSlowQueryDetail()}
              >
                More Details on {formatDateTime(tHappen - 15 * 60)} ~{' '}
                {formatDateTimeWithTz(tHappen + 5 * 60)}
              </Text>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  )
}

////////////////////////////////

const logItems = [
  {
    keyword: 'eviction manager: must evict pod(s) to reclaim memory',
    tips: 'Pod was evicted and restarted because of OOM'
  },
  {
    keyword: 'Out Of Memory Quota',
    tips: 'a single SQL statement exceeds tidb_mem_quota_query'
  },
  {
    keyword: 'memory exceeds quota',
    tips: 'flow control or disk spill behavior is triggered'
  },
  {
    keyword: 'the risk of OOM',
    tips: 'memory usage exceeds alarm ratio'
  }
]

const OOMLogs: React.FC<ItemCompProps> = ({ oomSummary, tHappen }) => {
  // const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()

  const detailLinks = useMemo(() => {
    return logItems.map((l, idx) => {
      const dataSource = `Loki-${curCluster?.provider}-${curCluster?.region}`
      let expr = ''
      if (idx === 0) {
        // {cluster="shoot/aws/ap-southeast-1/shoot--prod--b537b676", syslog="kubelet"} |= `eviction manager: must evict pod(s) to reclaim memory`
        const cluster = `${curCluster?.provider}-dataplane/shoot--prod--${curCluster?.shootName}`
        expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", cluster=\\"${cluster}\\", syslog=\\"kubelet\\"} |= \`${l.keyword}\``
      } else {
        // {namespace="tidb1379661944629574082", instance="db-tidb-0"} |= `Out Of Memory Quota`
        expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", namespace=\\"tidb${oomSummary?.payload.cluster_id}\\", instance=\\"${oomSummary?.payload.instance}\\"} |= \`${l.keyword}\``
      }

      return getLokiFullPath(
        dataSource,
        expr,
        (tHappen - 15 * 60) * 1000,
        (tHappen + 1) * 1000
      )
    })
  }, [tHappen, curCluster, oomSummary])

  return (
    <Table horizontalSpacing={0}>
      <thead>
        <tr>
          <th>Key Words</th>
          <th>More Details ({localTimeZoneStr})</th>
        </tr>
      </thead>
      <tbody>
        {logItems.map((l, idx) => (
          <tr key={l.keyword}>
            <td>
              <Group spacing='xs'>
                {l.keyword}
                <Tooltip withArrow multiline position='right' label={l.tips}>
                  <ThemeIcon variant='default' size='xs' radius='xl'>
                    <IconQuestionMark />
                  </ThemeIcon>
                </Tooltip>
              </Group>
            </td>
            <td>
              <Text
                className='underline'
                component='a'
                href={detailLinks[idx]}
                target='_blank'
                onClick={() =>
                  oomInsightTelemetry.clickLokiLogsDetail({
                    keyword: l.keyword
                  })
                }
              >
                More Details on {formatDateTime(tHappen - 15 * 60)} ~{' '}
                {formatDateTime(tHappen)}
              </Text>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

////////////////////////////////

const OOMProfiling: React.FC<ItemCompProps> = ({ tHappen }) => {
  const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()

  return (
    <Table horizontalSpacing={0}>
      <tbody>
        <tr>
          <td>From OOM Files</td>
          <td>
            {tHappen > 0 && (
              <DownloadLinkWithTooltip
                tHappen={tHappen}
                orgId={curOrg?.id!}
                cid={curCluster?.id!}
                forContent='profiling'
              />
            )}
          </td>
        </tr>
        <tr>
          <td>Continuous Profiling</td>
          <td>
            <Text
              className='underline'
              component='a'
              href={`dashboard/cloud/?orgId=${curOrg?.externalID!}&clusterId=${curCluster?.id!}#/continuous_profiling?from=${
                tHappen - 2 * 60 * 60
              }&to=${tHappen}&timeRangeType=absolute`}
              target='_blank'
              onClick={() => oomInsightTelemetry.clickConProfDetail()}
            >
              More Details Before {formatDateTimeWithTz(tHappen)}
            </Text>
          </td>
        </tr>
      </tbody>
    </Table>
  )
}

////////////////////////////////

const oomItems = [
  {
    itemKey: 'info',
    itemTitle: 'Insight Information',
    itemDetailComp: (props: ItemCompProps) => <OOMInfo {...props} />
  },
  {
    itemKey: 'metrics',
    itemTitle: 'Metrics',
    itemDetailComp: (props: ItemCompProps) => <OOMMetrics {...props} />
  },
  {
    itemKey: 'sqls',
    itemTitle: 'Related SQLs',
    itemDetailComp: (props: ItemCompProps) => <OOMSQLs {...props} />
  },
  {
    itemKey: 'profiling',
    itemTitle: 'Profiling',
    itemDetailComp: (props: ItemCompProps) => <OOMProfiling {...props} />
  },
  {
    itemKey: 'logs',
    itemTitle: 'Logs',
    itemDetailComp: (props: ItemCompProps) => <OOMLogs {...props} />
  }
  // {
  //   itemKey: 'configs',
  //   itemTitle: 'Configurations & Variables',
  //   itemDetailComp: (props: ItemCompProps) => <OOMConfigs {...props} />
  // }
]
const oomItemKeys = oomItems.map((i) => i.itemKey)

const Page: React.FC = () => {
  const { curOrg } = useCurOrg()
  const { curCluster } = useCurCluster()

  const { rid } = useParams<'rid'>()

  // oom summary
  const { data: oomSummaryRes } = useSWR(`/report/5/${rid}`, () =>
    getOOMSummary({
      orgId: curOrg!.externalID!,
      clusterId: curCluster!.id!,
      contextId: rid!
    })
  )

  // oom analysis event
  const oomSummary = useMemo(
    () =>
      oomSummaryRes?.data.activities?.filter(
        (a: any) => a.type === 'Action'
      )?.[0],
    [oomSummaryRes]
  )
  const instance = oomSummary?.payload.instance

  // oom or oom risk event
  const oomEvent = useMemo(
    () =>
      oomSummaryRes?.data.activities?.filter(
        (a: any) => a.type === 'Event'
      )?.[0],
    [oomSummaryRes]
  )

  // oom detail
  const { data: oomDetailRes } = useSWR(
    oomSummary ? `/reports/5/${rid}/detail` : null,
    () => getOOMDetail({ fileKey: oomSummary.payload.details.key })
  )

  // time
  const tHappen =
    oomSummary?.payload.occurance_time ?? // after the backend fix the api typo, then this line can be removed
    oomSummary?.payload.occurrence_time ??
    0

  const accordionItems = oomItems.map((i) => (
    <Accordion.Item
      key={i.itemKey}
      value={i.itemKey}
      className='bg-white border-gray-300'
    >
      <Accordion.Control>{i.itemTitle}</Accordion.Control>
      <Accordion.Panel>
        {i.itemDetailComp({
          oomSummary,
          oomDetail: oomDetailRes?.data,
          oomEvent,
          tHappen
        })}
      </Accordion.Panel>
    </Accordion.Item>
  ))

  return (
    <>
      <Header />

      <Stack className='m-auto max-w-[1440px] px-4 pb-8' spacing={0}>
        <Group position='apart' py='xl'>
          <Group className='items-baseline'>
            <Title order={2}>TiDB OOM Insight</Title>
            <div>
              <Skeleton visible={!instance}>
                <Text span color='dark' size='md'>
                  for{' '}
                </Text>
                <Link to={`/orgs/${curOrg?.id}/clusters/${curCluster?.id}`}>
                  <Text span color='red' size='md' fw='bold'>
                    {instance}
                  </Text>
                </Link>
                <Text span color='dark' size='md'>
                  {' '}
                  instance
                </Text>
              </Skeleton>
            </div>
          </Group>
          {tHappen > 0 && (
            <Tooltip
              maw={400}
              withArrow
              multiline
              position='top'
              label={downloadTooltip}
            >
              <Button
                onClick={() =>
                  oomInsightTelemetry.clickDownloadFiles({ source: 'title' })
                }
                variant='default'
                rightIcon={<IconInfoCircle size={16} />}
                component={Link}
                to={`/orgs/${curOrg?.id}/clusters/${
                  curCluster?.id
                }/files?beginTime=${
                  tHappen - 1 * 24 * 60
                }&endTime=${tHappen}&notEmpty=true`}
              >
                Download OOM Files
              </Button>
            </Tooltip>
          )}
        </Group>

        <Accordion
          multiple
          variant='separated'
          order={3}
          radius='sm'
          defaultValue={oomItemKeys}
        >
          {accordionItems}
        </Accordion>
      </Stack>
    </>
  )
}

export default Page
